import React, { useRef } from "react"
import { StaticImage } from "gatsby-plugin-image"
import { Link } from "gatsby"
import videoWeb from "../assets/videos/pushfusion-intro.webm"
import video from "../assets/videos/pushfusion-intro.mp4"
import {
  Layout,
  Seo,
  HeaderSvg,
  ListAndImageGrid,
  Features,
  GridList,
  HowItWorks,
  Partners,
  ListAndImage,
  Cta,
  BookADemoButton,
  Nav,
  HeaderContent,
} from "../components"
import { segments, mobile, dashboard, metadata } from "../data/main"
import Icon from "../components/icon"
import { useOnScreen } from "../utils/hooks"

export default function Home() {
  const ref = useRef()
  const isHeaderVisible = useOnScreen(ref)

  const features = [
    {
      name: "Emergency",
      icon: (
        <Icon
          className="h-20 w-20 text-blue-50"
          aria-hidden="true"
          color="#0E98FA"
          icon="siren"
        />
      ),
      text: "Real-time emergency lighting compliance status across your entire estate",
      link: "/platform/emergency",
    },
    {
      name: "Sensors",
      icon: (
        <Icon
          className="h-20 w-20 text-blue-50"
          aria-hidden="true"
          color="#0E98FA"
          icon="temperature-control"
        />
      ),
      text: "Keep on top of air quality in all your buildings at a glance",
      link: "/platform/sensors",
    },
    {
      name: "Casambi",
      icon: (
        <StaticImage
          src="../assets/images/casambi/casambi-badge-dark.png"
          alt="Building overview of pushfusion"
          placeholder="tracedSVG"
          // layout="fixed"
          quality={100}
          // height={20}
          loading="eager"
          className="w-16 h-auto mb-4"
        />
      ),
      text: "Pull data from your Casambi networks and feed your business intelligence reporting",
      link: "/platform/casambi",
    },
  ]
  return (
    <Layout>
      <Seo
        title="The estate data you need, at your fingertips"
        description={metadata}
      />
      <div>
        <Nav isHeaderVisible={isHeaderVisible} />
        <div className="relative bg-gray-900 overflow-hidden" ref={ref}>
          <HeaderSvg otherClasses="top-16 left-1/2 transform translate-x-48 translate-y-2 opacity-20" />
          <div className="relative pt-6 pb-16 sm:pb-24 lg:pb-32">
            <HeaderContent
              // title={{
              //   firstSentence: "The estate data you need,",
              //   secondSentence: "at your fingertips",
              // }}
              // small={true}
              title={{
                firstSentence: "Smart",
                secondSentence: "Monitoring",
              }}
              xLarge={true}
              description={
                <>
                  <span className="text-blue-50 font-bold">push</span>
                  <span className="text-pushfusion font-extrabold">
                    fusion
                  </span>{" "}
                  is a secure, cloud-based dashboard that bridges the gap
                  between the different hardware vendors in your buildings and
                  brings the information you need from their systems together to
                  save money on your compliance reporting and improve your
                  decision making alike.
                </>
              }
              button={<BookADemoButton />}
              media={
                <div className="relative mx-auto lg:max-w-xl">
                  <video
                    width="640"
                    autoPlay
                    controls
                    loop
                    id="vid"
                    muted
                    className="rounded-xl shadow-xl"
                  >
                    <source src={videoWeb} type="video/webm" />
                    <source src={video} type="video/mp4" />
                  </video>
                </div>
              }
            />
          </div>
        </div>
        <Features
          data={features}
          title={{
            firstSentence: "Facilities data",
            secondSentence: " at your fingertips",
          }}
          description="Are you ready to revolutionise your estate-wide monitoring?"
          position="middle"
          isMainSection={true}
        />
        <HowItWorks
          title={{ firstSentence: "How it", secondSentence: "works" }}
          description={
            <>
              To get started, simply install a{" "}
              <span className="text-gray-700 font-bold">push</span>
              <span className="text-pushfusion font-extrabold">
                fusion
              </span>{" "}
              gateway on the same network as the devices you want to monitor. It
              will configure itself and you’re good to go!
            </>
          }
          position="middle"
          image={
            <StaticImage
              src="../assets/images/main/indexDiagramCloudService.png"
              objectFit="cover"
              quality={100}
              width={1280}
              aspectRatio={4 / 3}
              placeholder="tracedSVG"
              alt="pushfusion cloud diagram"
            />
          }
        />
        <ListAndImageGrid
          data={dashboard}
          position="start"
          backgroundColor="bg-gray-50"
          title={{
            firstSentence: "Stunning",
            secondSentence: "dashboard",
          }}
          description={
            <div className="text-justify">
              <span className="text-gray-700 font-bold">push</span>
              <span className="text-pushfusion font-extrabold">
                fusion
              </span>{" "}
              provides a single secure interface to monitor and maintain your
              entire estate. The intuitive user interface allows users to
              quickly get to the information they need, whether that’s{" "}
              <Link
                to="/platform/emergency"
                className="text-pushfusion hover:text-pushfusion-darker "
              >
                emergency
              </Link>
              ,{" "}
              <Link
                to="/platform/sensors"
                className="text-pushfusion hover:text-pushfusion-darker "
              >
                sensors
              </Link>{" "}
              or{" "}
              <Link
                to="/platform/casambi"
                className="text-pushfusion hover:text-pushfusion-darker "
              >
                Casambi devices
              </Link>
              .
            </div>
          }
          mainImage={
            <StaticImage
              src="../assets/images/main/indexDashboardMain.png"
              alt="Image of pushfusion interface on a desktop showing Casambi devices and a mack book with a pushfusion report."
              placeholder="blurred"
              layout="constrained"
              quality={100}
              loading="eager"
              className="w-full h-full object-center object-cover rounded-lg"
            />
          }
          secondaryImage1={
            <StaticImage
              src="../assets/images/main/indexDashboardSecondary1.png"
              alt="Image of a map from pushfusion interface showing buildings with compliance issues."
              placeholder="blurred"
              layout="constrained"
              quality={100}
              loading="eager"
              className="w-full h-full object-center object-cover rounded-lg"
            />
          }
          secondaryImage2={
            <StaticImage
              src="../assets/images/main/indexDashboardSecondary2.png"
              alt="Image of pushfusion interface showing a list of devices in failure."
              placeholder="blurred"
              layout="constrained"
              quality={100}
              loading="eager"
              className="w-full h-full object-center object-cover rounded-lg"
            />
          }
        />
      </div>

      <ListAndImage
        data={mobile}
        title={{
          firstSentence: (
            <>
              <span className="text-gray-700 font-bold">push</span>
              <span className="text-pushfusion font-extrabold">fusion</span>
            </>
          ),
          secondSentence: <span className="block">mobile app</span>,
        }}
        description={
          <div className="text-justify">
            Guide your field engineers to their faults as they arrive onsite.
            The <span className="text-gray-900 font-bold">push</span>
            <span className="text-pushfusion font-extrabold">fusion</span>{" "}
            mobile app makes it easier for your field engineers to maximise
            their time onsite, ultimately causing less disruption for the client
            and other building users at the same time.
          </div>
        }
        mainImage={
          <>
            <div className="hidden lg:block lg:absolute lg:w-1/2 lg:h-full lg:pr-4 xl:pr-16">
              <StaticImage
                src="../assets/images/main/indexMobileImage.png"
                alt="Image of two mobile devices showing pushfusion app in a vertical position. The one on the left is showing building list and the on the right is showing the same list but on a digital map."
                placeholder="tracedSVG"
                layout="constrained"
                quality={100}
                height={1487}
                loading="eager"
                className="rounded-lg object-center object-cover"
              />
            </div>
            <div className="block w-2/3 lg:hidden">
              <StaticImage
                src="../assets/images/main/indexMobileImageHorizontal.png"
                alt="Image of two mobile devices showing pushfusion app. The one on the left is showing building list and the on the right is showing the same list but on a digital map."
                placeholder="tracedSVG"
                layout="constrained"
                quality={100}
                loading="eager"
                className="rounded-lg  object-center object-cover"
              />
            </div>
          </>
        }
        extraContent={
          <>
            <div className="inline-flex rounded-md shadow">
              <a
                href="https://apps.apple.com/gb/app/pushfusion/id1600240007"
                target="_blank"
                rel="noreferrer"
              >
                <StaticImage
                  src="../assets/images/main/app-store-lg.png"
                  alt="apple app store logo"
                  placeholder="blurred"
                  blurredOptions=""
                  layout="constrained"
                  quality={100}
                  height={50}
                  loading="eager"
                />
              </a>
            </div>
            <div className="ml-3 inline-flex">
              <a
                href="https://play.google.com/store/apps/details?id=io.pushfusion.mobile"
                target="_blank"
                rel="noreferrer"
              >
                <StaticImage
                  src="../assets/images/main/google-play-lg.png"
                  alt="apple app store logo"
                  placeholder="blurred"
                  blurredOptions=""
                  layout="constrained"
                  quality={100}
                  height={50}
                  loading="eager"
                />
              </a>
            </div>
          </>
        }
      />

      <GridList
        description={
          <>
            No matter the sector,{" "}
            <span className="text-gray-900 font-bold">push</span>
            <span className="text-pushfusion font-extrabold">fusion</span> can
            help you streamline your compliance processes. It's the ideal
            solution for facility managers, building owners and businesses
            alike.
          </>
        }
        title={{
          firstSentence: "Where does",
          secondSentence: "it work?",
        }}
        position="start"
        features={segments}
        backgroundColor="bg-gray-50"
      />

      <Partners />
      <Cta
        title="Interested in learning more about"
        subtitle="the benefits of pushfusion?"
        button1={
          <BookADemoButton
            background="bg-gray-50"
            backgroundHover="bg-gray-300"
            textColor="text-pushfusion"
          />
        }
      />
    </Layout>
  )
}
